import { useState } from "react";
import { useNotify, useRefresh } from "ra-core";
import { Bot, Loader2, MessageSquare, RefreshCw, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useSMS } from "./useSMS";

interface SMSComposerProps {
    contactId: number;
    contactName?: string;
    className?: string;
}

export const SMSComposer = ({ contactId, contactName, className }: SMSComposerProps) => {
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const notify = useNotify();
    const refresh = useRefresh();

    const {
        message,
        setMessage,
        isGenerating,
        isSending,
        error,
        generateAIMessage,
        sendSMS,
        characterCount,
        isOverLimit,
    } = useSMS(contactId);

    const handleSend = async () => {
        setShowConfirmDialog(false);
        const success = await sendSMS();
        if (success) {
            notify("SMS sent successfully", { type: "success" });
            refresh();
        } else {
            notify(error || "Failed to send SMS", { type: "error" });
        }
    };

    const handleGenerateClick = async () => {
        await generateAIMessage();
        if (error) {
            notify(error, { type: "error" });
        }
    };

    const segmentCount = Math.ceil(characterCount / 160) || 0;
    const isMultiSegment = segmentCount > 1;

    return (
        <div className={cn("space-y-3 p-4 border rounded-lg bg-muted/30", className)}>
            {/* Header */}
            <div className="flex items-center gap-2 text-sm font-medium">
                <MessageSquare className="w-4 h-4 text-blue-600" />
                <span>Send SMS</span>
            </div>

            {/* Text Area */}
            <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your message or generate with AI..."
                rows={4}
                className={cn(
                    "resize-none",
                    isOverLimit && "border-orange-500 focus-visible:ring-orange-500"
                )}
                disabled={isGenerating || isSending}
            />

            {/* Character Count */}
            <div className="flex justify-between items-center text-xs">
                <span className={cn(
                    "text-muted-foreground",
                    isOverLimit && "text-orange-600 font-medium"
                )}>
                    {characterCount}/160 characters
                    {isMultiSegment && (
                        <span className="ml-1">
                            ({segmentCount} segments = {segmentCount}× cost)
                        </span>
                    )}
                </span>
                {error && (
                    <span className="text-destructive">{error}</span>
                )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={handleGenerateClick}
                    disabled={isGenerating || isSending}
                    className="gap-1.5"
                >
                    {isGenerating ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Generating...
                        </>
                    ) : (
                        <>
                            <Bot className="w-4 h-4" />
                            Generate AI Message
                        </>
                    )}
                </Button>

                {message && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleGenerateClick}
                        disabled={isGenerating || isSending}
                        className="gap-1.5"
                    >
                        <RefreshCw className="w-4 h-4" />
                        Regenerate
                    </Button>
                )}

                <div className="flex-1" />

                <Button
                    size="sm"
                    onClick={() => setShowConfirmDialog(true)}
                    disabled={!message.trim() || isGenerating || isSending}
                    className="gap-1.5"
                >
                    {isSending ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Sending...
                        </>
                    ) : (
                        <>
                            <Send className="w-4 h-4" />
                            Send SMS
                        </>
                    )}
                </Button>
            </div>

            {/* Confirmation Dialog */}
            <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Confirm SMS</DialogTitle>
                        <DialogDescription>
                            Send this message to {contactName || "this contact"}?
                        </DialogDescription>
                    </DialogHeader>
                    <div className="p-3 bg-muted rounded-md text-sm my-4">
                        {message}
                    </div>
                    <p className="text-xs text-muted-foreground">
                        {characterCount} characters • {segmentCount} segment{segmentCount !== 1 ? "s" : ""}
                    </p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleSend} disabled={isSending}>
                            {isSending ? "Sending..." : "Send SMS"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
